import {
  IRegistryParameters,
  IComponentLoader,
  IComponentBundleLoader,
  IHostContext,
} from '../../registry.types';

import { createComponentLoader } from '../../loaders/createComponentLoader';
import { registerComponents } from './registerComponents';

export interface ILazyRegistryParameters {
  contexts: Array<IHostContext>;
  bundleLoaders: Record<string, IComponentBundleLoader>;
}

function createComponentLoaders<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
>({
  contexts,
  bundleLoaders,
}: {
  contexts: Array<IHostContext>;
  bundleLoaders: Record<string, IComponentBundleLoader>;
}): Array<{
  context: IHostContext;
  components: Record<string, IComponentLoader<TComponentModel, TParts>>;
}> {
  return contexts.map(context => {
    const components: Record<
      string,
      IComponentLoader<TComponentModel, TParts>
    > = {};

    Object.keys(context.manifest.components).forEach(componentName => {
      const component = context.manifest.components[componentName];

      components[componentName] = createComponentLoader({
        loadBundle: bundleLoaders[context.library.artifactId],
        component,
        statics: {
          ...context.statics,
          ...context.manifest.statics[componentName],
        },
      });
    });

    return {
      context,
      components,
    };
  });
}

/**
 * Main
 */
export function createLazyEntry<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
>({ contexts, bundleLoaders }: ILazyRegistryParameters) {
  return async (params: IRegistryParameters<TComponentModel, TParts>) => {
    const componentLoaders = createComponentLoaders<TComponentModel, TParts>({
      contexts,
      bundleLoaders,
    });

    componentLoaders.forEach(({ components, context }) => {
      registerComponents<TComponentModel, TParts>(components, context, {
        registerComponent: params.registerComponent,
        registerLibrary: params.registerLibrary,
      });
    });
  };
}
